import EditIcon from '@rsuite/icons/Edit'
import PlusIcon from '@rsuite/icons/Plus'
import {useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import IconButton from 'rsuite/esm/IconButton'
import * as homepageSettingRedux from '../index'
import {service} from '../redux/service'

type Props = {
  open: boolean
  handleClose: () => void
}

// Standalone (not wired through the redux-saga pipeline like the rest of this
// module) — kept deliberately self-contained since this is a small, low-traffic
// admin feature: creating/editing the fixed list of Section options that the
// "Section" dropdown above pulls from, not the homepage content rows themselves.
const ManageHomepageSectionModal = ({open, handleClose}: Props) => {
  const dispatch = useDispatch()
  const {homePageSection} = useSelector((state: any) => state.homepageSetting)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [systemName, setSystemName] = useState('')
  const [saving, setSaving] = useState(false)

  const startAdd = () => {
    setEditingId('__new__')
    setName('')
    setSystemName('')
  }

  const startEdit = (section: any) => {
    setEditingId(section.id)
    setName(section.name)
    setSystemName(section.systemName)
  }

  const cancelForm = () => setEditingId(null)

  const handleSave = async () => {
    if (!name.trim() || !systemName.trim()) {
      toast.error('Name and System Name are required')
      return
    }
    setSaving(true)
    try {
      if (editingId === '__new__') {
        await service.addHomepageSection({name, systemName})
        toast.success('Section created successfully')
      } else {
        await service.updateHomepageSection({name, systemName}, editingId as string)
        toast.success('Section updated successfully')
      }
      dispatch(homepageSettingRedux.actions.getHomepageSection())
      setEditingId(null)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Something went wrong')
    } finally {
      setSaving(false)
    }
  }

  const handleModalClose = () => {
    setEditingId(null)
    handleClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      backdrop='static'
      keyboard={false}
      data-cy='manage-homepage-section-modal'
    >
      <Modal.Header>
        <Modal.Title>Manage Homepage Sections</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <table className='table'>
          <thead>
            <tr>
              <th>Name</th>
              <th>System Name</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {homePageSection?.map((section: any) => (
              <tr key={section.id}>
                <td>{section.name}</td>
                <td>
                  <code>{section.systemName}</code>
                </td>
                <td className='text-end'>
                  <IconButton
                    size='xs'
                    icon={<EditIcon />}
                    onClick={() => startEdit(section)}
                    data-cy={`edit-section-${section.id}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {editingId ? (
          <div className='border rounded p-3 mt-3'>
            <div className='form-group mb-2'>
              <label>Name</label>
              <input
                className='form-control'
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-cy='section-name-field'
              />
            </div>
            <div className='form-group mb-2'>
              <label>System Name</label>
              <input
                className='form-control'
                value={systemName}
                onChange={(e) => setSystemName(e.target.value)}
                data-cy='section-system-name-field'
              />
              <small className='text-muted'>
                Used internally to match content to this section — changing it after content
                already references it will stop that content from showing.
              </small>
            </div>
            <div className='d-flex justify-content-end' style={{gap: '0.5rem'}}>
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={cancelForm}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={saving}
                data-cy='section-save-button'
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type='button'
            className='btn btn-success mt-3'
            onClick={startAdd}
            data-cy='add-new-section-button'
          >
            <PlusIcon /> Add New Section
          </button>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default ManageHomepageSectionModal
